import { Link } from "@tanstack/react-router";
import { Lock, ExternalLink } from "lucide-react";

import type { DerivedCoverageResult } from "@/application/use-cases/products/derive-coverage";

interface DerivedScoreBadgeProps {
  result: DerivedCoverageResult;
  accentColor: string;
  /** Optional deep-link query into the matrix, e.g. `?product=<slug>` */
  matrixLinkSearch?: { product: string };
}

/**
 * Replaces ScorePicker for criteria with derivation_kind = 'derived-coverage'.
 * Shows the computed score (or "—" when null), a Locked pill, and a link to
 * the matrix. Tooltip on hover surfaces the live formula.
 */
export function DerivedScoreBadge({
  result,
  accentColor,
  matrixLinkSearch,
}: DerivedScoreBadgeProps) {
  const hasScore = result.score !== null;
  const display = hasScore ? (result.score as number).toFixed(1) : "—";
  const formula = formatFormula(result);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span
          className="font-data inline-flex h-8 min-w-10 items-center justify-center rounded-md border px-2 text-[13px] font-medium tabular-nums"
          style={
            hasScore
              ? { borderColor: accentColor, background: `${accentColor}1a`, color: accentColor }
              : { borderColor: "oklch(0.91 0.008 240)", color: "oklch(0.62 0.01 240)" }
          }
          title={formula}
        >
          {display}
        </span>
        <span
          className="font-data inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-medium tracking-[0.10em] uppercase"
          style={{
            background: "oklch(0.95 0.005 240)",
            color: "oklch(0.45 0.01 240)",
          }}
          title="Score is auto-derived from feature coverage assignments and cannot be edited here."
        >
          <Lock className="size-2.5" />
          locked · auto
        </span>
      </div>
      <Link
        to="/coverage-matrix"
        search={matrixLinkSearch}
        className="text-foreground/60 hover:text-foreground inline-flex w-fit items-center gap-1 text-[11px] transition-colors"
      >
        View feature mapping
        <ExternalLink className="size-2.5" />
      </Link>
    </div>
  );
}

function formatFormula(r: DerivedCoverageResult): string {
  if (r.distinctDomains !== undefined) {
    return `Domain breadth: ${r.distinctDomains} / 8 domains → score ${r.score?.toFixed(2) ?? "—"} / 5`;
  }
  if (r.score === null) {
    return r.inScopeCount === 0
      ? "0 features in scope for this criterion"
      : `${r.inScopeCount} features in scope · 0 assigned yet`;
  }
  const parts: string[] = [];
  if (r.nativeCount > 0) parts.push(`${r.nativeCount} native (×1.0)`);
  if (r.pluginCount > 0) parts.push(`${r.pluginCount} plugin (×0.7)`);
  if (r.customCount > 0) parts.push(`${r.customCount} custom (×0.4)`);
  return `${parts.join(" + ")} = ${r.weightedSum.toFixed(2)} / ${r.inScopeCount} in scope = ${(r.ratio ?? 0).toFixed(2)} → score ${r.score.toFixed(2)} / 5`;
}
